import moment from "moment";

// Utility function to extract property based on key
export const extractProperty = (val, key) => {
    if (typeof val === "object" && val?.[key]) {
        return val[key];
    }
    return val;
};

// Utility function to normalize values by extracting a specific property
export const normalizeValues = (values, key) =>
    Array.isArray(values)
        ? values.map((val) => extractProperty(val, key))
        : [extractProperty(values, key)];

export const AVATAR_COLORS = [
    "#BBBEFF",
    "#8BDBCD",
    "#F9E289",
    "#40CDFA",
    "#F58D91",
    "#C8C9CB",
    "#CEC7B6",
];

export const getAvatarColor = (firstLetter) => {
    if (!firstLetter) return AVATAR_COLORS[0];

    const char = firstLetter.toString().charAt(0).toUpperCase();
    let index;

    if (/[0-9]/.test(char)) {
        index = parseInt(char);
    } else {
        index = char.charCodeAt(0) - 65;
    }

    const colorIndex = Math.abs(index) % AVATAR_COLORS.length;
    return AVATAR_COLORS[colorIndex];
};

export const formatLabel = (label) => {
    const formattedLabel = label
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    return formattedLabel;
};

export const convertToDateRange = (dateRange) => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
        return { startDate: null, endDate: null };
    }

    // If already Date objects, return as is
    if (dateRange.startDate instanceof Date && dateRange.endDate instanceof Date) {
        return dateRange;
    }

    // Return the original string format to avoid double conversion
    return {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
    };
};

export const convertToDates = (dateRange) => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
        return { startDate: null, endDate: null };
    }

    // If already in string format, return as is
    if (typeof dateRange.startDate === 'string' && typeof dateRange.endDate === 'string') {
        // Check if already in DD-MM-YYYY format
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateRange.startDate) &&
            /^\d{2}-\d{2}-\d{4}$/.test(dateRange.endDate)) {
            return dateRange;
        }
    }

    // Otherwise, convert to DD-MM-YYYY format
    return {
        startDate: moment(dateRange.startDate).format("DD-MM-YYYY"),
        endDate: moment(dateRange.endDate).format("DD-MM-YYYY")
    };
};

export const getAnalystFilters = (analystList, setFilters) => {
    if (analystList) {
        return {
            analyst: {
                name: "analystName",
                label: "Analyst Name",
                key: "analyst",
                options: analystList.map((analyst) => ({
                    label: analyst.username,
                    id: analyst.id,
                })),
                multiSelect: true,
            },
        };
    }
    return null;
};
export const getDynamicFilterOptions = (
    dynamicFilterOptions,
    setFilters,
    setStaticFiltersData
) => {
    if (dynamicFilterOptions) {
        const dynamicFilters = Object.keys(dynamicFilterOptions)?.map(
            (filterKey) => ({
                name: filterKey,
                label: formatLabel(filterKey),
                key: filterKey,
                options: dynamicFilterOptions[filterKey]?.map((option) => {
                    if (typeof option === "object" && option !== null) {
                        return {
                            label: option.label || option.id || "", // Fallback to id if label is not available
                            id: option.id || option.label || "", // Fallback to label if id is not available
                        };
                    } else {
                        // Use option directly if it's a primitive value
                        return {
                            label: option,
                            id: option,
                        };
                    }
                }),
                multiSelect: true,
            })
        );
        if (dynamicFilters.length) {
            setStaticFiltersData &&
                setStaticFiltersData((filters) => [
                    ...filters,
                    ...dynamicFilters,
                ]);
            setFilters((filters) => [...filters, ...dynamicFilters]);
        }
    }
};

export const getHighlightStyle = (color) => {
    const styles = {
        RED: {
            backgroundColor: "#fff1f1",
            borderColor: "#FF0000",
        },
        AMBER: {
            backgroundColor: "#FFF8E7",
            borderColor: "#FFA500",
        },
        white: {
            backgroundColor: "white",
            borderColor: "white",
        },
    };
    return (
        styles[color] || {
            backgroundColor: "white",
            borderColor: "white",
        }
    );
};

export const customCellColor = (header, rowdata, columnTypeMapping) => {
    for (const [type, { columns, colorKey }] of Object.entries(
        columnTypeMapping
    )) {
        if (
            columns.includes(header.key) &&
            header.key === "corporate_office_visits"
        ) {
            const colorValue = rowdata[colorKey];
        }
        if (columns.includes(header.key)) {
            const colorValue = rowdata[colorKey];
            return !!colorValue ? getHighlightStyle(colorValue) : null;
        }
    }
    return null;
};

export const getUserAvatar = (profilePictureUrl, name) => {
    const avatar = profilePictureUrl ? (
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-black font-medium min-w-8 min-h-8">
            <img
                src={profilePictureUrl}
                alt={name}
                className="w-8 h-8 rounded-full p-0.5"
            />
        </div>
    ) : (
        <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-black font-medium min-w-8 min-h-8"
            style={{ background: getAvatarColor(name) }}
        >
            {name
                .split(" ")
                .map((n) => n[0])
                .join("")}
        </div>
    );
    return avatar;
};

export const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
};
export const formatCurrencyFromMillions = (value) => {
    const num = parseFloat(value) * 100000; // Multiply by 100,000
    if (isNaN(num)) return "0";

    // Convert to Trillions if ≥ 1,000 Billion (1 Trillion)
    if (num >= 1_000_000_000_000) {
        return `${(num / 1_000_000_000_000).toFixed(1)} Tn`; // Trillions
    }

    // Convert to Billions if ≥ 1 Billion (1,000 Million)
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(1)} Bn`; // Billions
    }

    // Convert to Millions if ≥ 1 Million
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(1)} Mn`; // Millions
    }

    // Return in base format if less than 1 Million
    return `${num.toFixed(1)}`;
};

export const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0";

    // Convert to Trillions if ≥ 1,000 Billion (1 Trillion)
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)} Tn`; // Trillions
    }

    // Convert to Billions if ≥ 1,000 Million (1 Billion)
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)} Bn`; // Billions
    }

    // Otherwise, keep it in Millions
    return `${num.toFixed(1)} Mn`;
};

